import {
  DealType,
  ICheckout,
  IConfiguration,
  ILocation,
  IPhoto,
  Maybe,
  StorageTime
} from 'src/types/graphql'
import { getEffectiveDealType } from 'src/utils/pricing'

/**
 * Returns the cover photo for a location. Prioritizes `location.coverPhoto`
 * when set; otherwise falls back to the default config's cover photo, then
 * the first photo in the gallery.
 *
 * NOTE: on GraphQL-served surfaces `location.coverPhoto` is already
 * config-aware — the backend `coverPhoto(searchInput)` field resolver
 * returns the pillar/search-matched config's own cover (search cards pass
 * `searchInput` via CARD_LOCATION_FIELDS; listing pages resolve with an
 * explicit `configurationId`), keeping the host's curated location cover
 * when both point at the same underlying image. This helper is just the
 * client-side fallback chain on top of that — don't re-implement per-config
 * precedence here (card fragments deliberately omit `photos[].id`).
 */
export function getCoverPhoto(
  location: Maybe<ILocation> | undefined
): IPhoto | undefined {
  if (!location) {
    return undefined
  }

  const { coverPhoto, photos, defaultConfiguration } = location

  // Prioritize location-level cover photo (host-curated)
  if (coverPhoto) return coverPhoto

  // Fall back to config-level cover photo
  const { coverPhotoId } = defaultConfiguration ?? {}
  if (coverPhotoId) {
    const configCover = photos?.find((photo) => photo.id === coverPhotoId)
    if (configCover) return configCover
  }

  return photos?.[0] ?? undefined
}

export function getCoverPhotoUrl(
  location: Maybe<ILocation> | undefined
): string {
  if (!location) {
    return '/images/empty.jpeg'
  }

  const cover = getCoverPhoto(location)
  return cover?.url ?? '/images/empty.jpeg'
}

/**
 * Minimum stay (months) for the standard `discountEnabled` first-month
 * discount — mirrors the server's `getValidDiscountRate`
 * (spotme-server/src/api/location/discounts.ts), which is what the checkout
 * actually bills.
 */
export const STANDARD_DISCOUNT_MIN_MONTHS = 6

export function discountMeetsMinimum(
  storageTime: Maybe<StorageTime> | undefined,
  minMonths: Maybe<number> | undefined
): boolean {
  const min = minMonths ?? STANDARD_DISCOUNT_MIN_MONTHS
  if (!storageTime) return true
  if (storageTime === StorageTime.Month) return 1 >= min
  if (storageTime === StorageTime.FewMonths) return 1 >= min
  if (storageTime === StorageTime.FewMonthsSix) return 3 >= min
  if (storageTime === StorageTime.FewMonthsTwelve) return 6 >= min
  if (storageTime === StorageTime.Year) return 12 >= min
  if (storageTime === StorageTime.FewYears) return 13 >= min
  if (storageTime === StorageTime.Indefinitely) return true
  return true
}

/**
 * Card-pill copy for the first-month discount. ONE wording for every card:
 * it must say «1er mes» (a bare «50% off» reads as half the monthly rent —
 * renters quoted «$281 al mes» back to the chatbot, 2026-08/09) and the
 * minimum stay.
 */
export const formatDiscountPill = (discount: {
  percentage: number
  minMonths: number
}): string => {
  const { headline, condition } = discountPillParts(discount)
  return `${headline} · ${condition}`
}

/**
 * The same pill copy in its two halves, for a pill too narrow for one line
 * (map card at 320px): it breaks BETWEEN the halves, never inside one, so
 * the minimum stay can't be cut off or orphaned.
 */
export const discountPillParts = (discount: {
  percentage: number
  minMonths: number
}): { headline: string; condition: string } => ({
  headline: `${discount.percentage}% 1er mes`,
  condition: `${discount.minMonths}+ meses`
})

/**
 * The first-month discount a surface may show. Without a `storageTime` it
 * returns the listing's discount unconditionally — so every surface that
 * renders it before the renter picked a duration MUST also render
 * `minMonths` ("6+ meses"), or it advertises a price the checkout won't bill
 * (thread 11221748059, 2026-09-23: a 2-month renter saw −50% on the listing
 * and lost it at checkout).
 */
export function getDiscount(
  configuration: Maybe<IConfiguration> | undefined,
  isBrokerMode: boolean,
  checkout?: Maybe<Pick<ICheckout, 'storageTime'>> | undefined
): {
  percentage: number
  description: string
  colorBg: string
  colorText: string
  /** Text colour for the pill when it overlays a PHOTO on a white-500/95
   *  chip (search + recommended cards). `colorBg`/`colorText` are for the
   *  in-copy pill; the pale green bg is invisible on bright photos. */
  overlayText: string
  minMonths: number
} | null {
  if (!configuration || isBrokerMode) return null
  // SpotMe's discounts are Stripe coupons on an on-platform (SpotMe Now)
  // checkout. On Traditional the operator bills the renter, so showing
  // SpotMe's 50% there promises a price nobody honors (Mex Storage,
  // 2026-09-23 — and the chatbot had to deny it). An operator's OWN promotion
  // is `configuration.operatorPromo`, rendered separately. Mirrors the
  // server's `hasSpotMeFirstMonthDiscount` (api/location/discounts.ts).
  if (getEffectiveDealType(configuration) !== DealType.Monthly) return null
  const { storageTime } = checkout ?? {}

  const { specialDiscount, discountEnabled } = configuration ?? {}

  if (!specialDiscount && !discountEnabled) return null
  const isValid = specialDiscount?.expiresAt
    ? new Date(specialDiscount.expiresAt) > new Date()
    : false

  if (
    specialDiscount &&
    isValid &&
    discountMeetsMinimum(storageTime, specialDiscount.minMonths)
  ) {
    return {
      percentage: (specialDiscount.discountMonths ?? 0) * 100,
      description: specialDiscount.description ?? '',
      colorBg: 'bg-red-500',
      colorText: 'text-white-500',
      overlayText: 'text-red-500',
      minMonths: specialDiscount.minMonths ?? STANDARD_DISCOUNT_MIN_MONTHS
    }
  }
  if (
    discountEnabled &&
    discountMeetsMinimum(storageTime, STANDARD_DISCOUNT_MIN_MONTHS)
  ) {
    return {
      percentage: 50,
      description: '1er mes',
      colorBg: 'bg-green-300',
      colorText: 'text-green-800',
      overlayText: 'text-green-800',
      minMonths: STANDARD_DISCOUNT_MIN_MONTHS
    }
  }
  return null
}
